'use client'

import {
  ArrowRight,
  Play,
  ShieldCheck,
  Star,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  BarChart2,
  Bell,
  CheckCircle2,
  Clock,
  Zap,
  HardDrive,
  Activity,
} from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useT } from '@/lib/useT'

/* ─── Mini-chart sparkline (SVG) ─────────────────────────────── */
function Sparkline({
  data,
  color,
  fill,
}: {
  data: number[]
  color: string
  fill: string
}) {
  const w = 80
  const h = 28
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w
      const y = h - ((v - min) / range) * (h - 4) - 2
      return `${x},${y}`
    })
    .join(' ')
  const areaBottom = `${w},${h} 0,${h}`
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts}
      />
      <polygon
        fill={fill}
        points={`${pts} ${areaBottom}`}
        opacity="0.18"
      />
    </svg>
  )
}

/* ─── DonutChart mini ─────────────────────────────────────────── */
function DonutMini({
  value,
  color,
}: {
  value: number
  color: string
}) {
  const r = 14
  const circ = 2 * Math.PI * r
  const dash = (value / 100) * circ
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden="true">
      <circle cx="18" cy="18" r={r} fill="none" stroke="#E2E4E5" strokeWidth="4" />
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="4"
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform="rotate(-90 18 18)"
      />
      <text
        x="18"
        y="22"
        textAnchor="middle"
        fontSize="8"
        fontWeight="700"
        fill={color}
      >
        {value}%
      </text>
    </svg>
  )
}

/* ─── Dashboard preview ────────────────────────────────────────── */
function DashboardPreview() {
  const t = useT()
  return (
    <div
      className="relative w-full rounded-[18px] overflow-hidden"
      style={{
        background: '#F5F7F8',
        boxShadow: '0 8px 16px rgba(10,23,29,0.08), 0 32px 64px rgba(10,23,29,0.16)',
      }}
    >
      {/* Window chrome */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[#16242B]">
        <span className="w-3 h-3 rounded-full bg-[#952E27] opacity-80" />
        <span className="w-3 h-3 rounded-full bg-[#FFBD76] opacity-80" />
        <span className="w-3 h-3 rounded-full bg-[#22614A] opacity-80" />
        <span className="ml-3 text-[10px] text-[#99B0B5] font-medium tracking-wide">
          Ajnihat ERP — Executive Dashboard
        </span>
      </div>

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#003F47] border-b border-[#99B0B5]/20">
        <div className="flex items-center gap-3">
          <img
            src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/new%20logo%20anajah-EudXMsd01h5rEwHdKcZADahoRi78xH.png"
            alt="Ajnihat Alnajah"
            className="h-5 w-auto"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-[#FFFBF7]/10">
            <Bell size={12} className="text-[#FFF6E9]" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#FFBD76]" />
          </span>
          <span className="w-6 h-6 rounded-full bg-[#FFBD76] flex items-center justify-center text-[9px] font-bold text-[#0A171D]">
            AK
          </span>
        </div>
      </div>

      {/* Dashboard body */}
      <div className="p-3 grid grid-cols-12 gap-2.5" style={{ background: '#F5F7F8' }}>

        {/* KPI row */}
        {[
          {
            labelKey: 'dashboard.revenue',
            value: '$2.4M',
            delta: '+18.2%',
            up: true,
            data: [40, 55, 48, 62, 58, 72, 80],
            color: '#003F47',
          },
          {
            labelKey: 'dashboard.pipeline',
            value: '$8.1M',
            delta: '+6.5%',
            up: true,
            data: [30, 38, 45, 42, 55, 60, 68],
            color: '#22614A',
          },
          {
            labelKey: 'dashboard.customers',
            value: '1,284',
            delta: '+4.1%',
            up: true,
            data: [20, 25, 28, 30, 26, 34, 38],
            color: '#1A535A',
          },
          {
            labelKey: 'dashboard.churn',
            value: '1.8%',
            delta: '-0.3%',
            up: false,
            data: [8, 6, 7, 5, 4, 5, 3],
            color: '#952E27',
          },
        ].map((kpi) => (
          <div
            key={kpi.labelKey}
            className="col-span-3 bg-white rounded-[12px] p-2.5"
            style={{ boxShadow: '0 1px 2px rgba(10,23,29,0.06), 0 2px 6px rgba(10,23,29,0.06)' }}
          >
            <p className="text-[9px] text-[#6C7377] font-medium uppercase tracking-wide mb-1">
              {t(kpi.labelKey as any)}
            </p>
            <p className="text-[15px] font-bold text-[#0A171D] leading-none">{kpi.value}</p>
            <div className="flex items-center justify-between mt-1.5">
              <span
                className={`flex items-center gap-0.5 text-[9px] font-semibold ${kpi.up ? 'text-[#22614A]' : 'text-[#952E27]'}`}
              >
                {kpi.up ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
                {kpi.delta}
              </span>
              <Sparkline data={kpi.data} color={kpi.color} fill={kpi.color} />
            </div>
          </div>
        ))}

        {/* Sales Pipeline (col-span-7) */}
        <div
          className="col-span-7 bg-white rounded-[12px] p-3"
          style={{ boxShadow: '0 1px 2px rgba(10,23,29,0.06), 0 2px 6px rgba(10,23,29,0.06)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-[#0A171D] uppercase tracking-wide">{t('dashboard.salesPipeline')}</p>
            <span className="text-[9px] text-[#1A535A] font-semibold bg-[#EEF7F2] px-2 py-0.5 rounded-full">{t('dashboard.q2_2025')}</span>
          </div>
          <div className="space-y-1.5">
            {[
              { stageKey: 'dashboard.prospecting', value: 68, amount: '$1.8M', color: '#2E9BD6' },
              { stageKey: 'dashboard.qualification', value: 52, amount: '$2.1M', color: '#27AE9A' },
              { stageKey: 'dashboard.proposal', value: 38, amount: '$1.4M', color: '#8DC63F' },
              { stageKey: 'dashboard.negotiation', value: 24, amount: '$0.9M', color: '#FFBD76' },
              { stageKey: 'dashboard.closedWon', value: 15, amount: '$0.7M', color: '#003F47' },
            ].map((s) => (
              <div key={s.stageKey} className="flex items-center gap-2">
                <p className="text-[8px] text-[#6C7377] w-20 shrink-0">{t(s.stageKey as any)}</p>
                <div className="flex-1 bg-[#E2E4E5] rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${s.value}%`, background: s.color }}
                  />
                </div>
                <p className="text-[9px] font-semibold text-[#0A171D] w-10 text-right shrink-0">{s.amount}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick stats (col-span-5) */}
        <div
          className="col-span-5 bg-white rounded-[12px] p-3"
          style={{ boxShadow: '0 1px 2px rgba(10,23,29,0.06), 0 2px 6px rgba(10,23,29,0.06)' }}
        >
          <p className="text-[10px] font-bold text-[#0A171D] uppercase tracking-wide mb-2">{t('dashboard.quickStats')}</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { labelKey: 'dashboard.openTickets', val: '34', icon: <Activity size={10} />, color: '#952E27', bg: '#FDF2F1' },
              { labelKey: 'dashboard.activeTasks', val: '128', icon: <CheckCircle2 size={10} />, color: '#22614A', bg: '#EEF7F2' },
              { labelKey: 'dashboard.teamMembers', val: '47', icon: <Users size={10} />, color: '#1A535A', bg: '#EDF6F8' },
              { labelKey: 'dashboard.meetingsToday', val: '6', icon: <Clock size={10} />, color: '#9C5800', bg: '#FEFBEE' },
            ].map((s) => (
              <div
                key={s.labelKey}
                className="flex items-center gap-2 p-1.5 rounded-[8px]"
                style={{ background: s.bg }}
              >
                <span
                  className="flex items-center justify-center w-5 h-5 rounded-md shrink-0"
                  style={{ background: s.color, color: '#fff' }}
                >
                  {s.icon}
                </span>
                <div>
                  <p className="text-[11px] font-bold text-[#0A171D] leading-none">{s.val}</p>
                  <p className="text-[8px] text-[#6C7377] mt-0.5">{t(s.labelKey as any)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity (col-span-7) */}
        <div
          className="col-span-7 bg-white rounded-[12px] p-3"
          style={{ boxShadow: '0 1px 2px rgba(10,23,29,0.06), 0 2px 6px rgba(10,23,29,0.06)' }}
        >
          <p className="text-[10px] font-bold text-[#0A171D] uppercase tracking-wide mb-2">{t('dashboard.recentActivity')}</p>
          <div className="space-y-1.5">
            {[
              { textKey: 'dashboard.activity1', time: '2m ago', icon: <DollarSign size={9} />, color: '#22614A', bg: '#EEF7F2' },
              { textKey: 'dashboard.activity2', time: '14m ago', icon: <BarChart2 size={9} />, color: '#9C5800', bg: '#FEFBEE' },
              { textKey: 'dashboard.activity3', time: '1h ago', icon: <Users size={9} />, color: '#1A535A', bg: '#EDF6F8' },
              { textKey: 'dashboard.activity4', time: '3h ago', icon: <CheckCircle2 size={9} />, color: '#003F47', bg: '#E4ECED' },
            ].map((a, i) => (
              <div key={i} className="flex items-start gap-2">
                <span
                  className="flex items-center justify-center w-4 h-4 rounded-md mt-0.5 shrink-0"
                  style={{ background: a.bg, color: a.color }}
                >
                  {a.icon}
                </span>
                <p className="text-[8.5px] text-[#16242B] leading-tight flex-1">{t(a.textKey as any)}</p>
                <p className="text-[8px] text-[#9EA2A5] shrink-0">{a.time}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Storage + API (col-span-5) */}
        <div
          className="col-span-5 bg-white rounded-[12px] p-3"
          style={{ boxShadow: '0 1px 2px rgba(10,23,29,0.06), 0 2px 6px rgba(10,23,29,0.06)' }}
        >
          <p className="text-[10px] font-bold text-[#0A171D] uppercase tracking-wide mb-2">{t('dashboard.systemHealth')}</p>
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center gap-1">
              <DonutMini value={62} color="#003F47" />
              <p className="text-[8px] text-[#6C7377]">{t('dashboard.storage')}</p>
            </div>
            <div className="flex flex-col items-center gap-1">
              <DonutMini value={34} color="#22614A" />
              <p className="text-[8px] text-[#6C7377]">{t('dashboard.api')}</p>
            </div>
            <div className="flex-1 space-y-1.5">
              <div>
                <div className="flex justify-between text-[8px] text-[#6C7377] mb-0.5">
                  <span>{t('dashboard.uptime')}</span><span className="text-[#22614A] font-semibold">99.98%</span>
                </div>
                <div className="bg-[#E2E4E5] rounded-full h-1 overflow-hidden">
                  <div className="h-full rounded-full bg-[#22614A]" style={{ width: '99.98%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[8px] text-[#6C7377] mb-0.5">
                  <span>{t('dashboard.response')}</span><span className="text-[#1A535A] font-semibold">42ms</span>
                </div>
                <div className="bg-[#E2E4E5] rounded-full h-1 overflow-hidden">
                  <div className="h-full rounded-full bg-[#1A535A]" style={{ width: '88%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

/* ─── Hero ─────────────────────────────────────────────────────── */
export default function Hero() {
  const t = useT()
  return (
    <section
      id="hero"
      className="hero-landing relative overflow-hidden bg-[#003F47]"
    >
      {/* Waveform accent bar */}
      <div className="absolute top-0 inset-x-0 h-[3px] gradient-wave" aria-hidden="true" />

      {/* Background radial texture */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(ellipse 60% 50% at 10% 30%, rgba(255,189,118,0.07) 0%, transparent 70%), radial-gradient(ellipse 50% 60% at 90% 70%, rgba(39,174,154,0.06) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-[1200px] px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* ── Left column ──────────────────────────────────────── */}
          <div className="flex flex-col gap-7">
            {/* Eyebrow */}
            <p className="inline-flex self-start items-center gap-2 px-4 py-1.5 rounded-[999px] border border-[#99B0B5]/40 bg-[#FFFBF7]/10 text-[#FFBD76] text-xs font-semibold uppercase tracking-[0.12em]">
              <Zap size={12} className="text-[#FFBD76]" />
              {t('hero.badge')}
            </p>

            {/* Headline */}
            <h1 className="text-[2.75rem] md:text-[3.25rem] lg:text-[3.75rem] font-bold text-[#FFFBF7] leading-[1.08] tracking-[-0.02em] text-balance">
              {t('hero.headline')}{' '}
              <br className="hidden sm:block" />
              <span className="text-[#FFBD76]">{t('hero.headlineGold')}</span>
            </h1>

            {/* Sub-headline */}
            <p className="text-lg text-[#FFF6E9]/75 leading-[1.7] max-w-lg">
              {t('hero.description')}
            </p>

            {/* CTA group */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="#signup"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-[999px] bg-[#FFBD76] text-[#0A171D] font-semibold text-base hover:bg-[#F0A24E] active:translate-y-px transition-all duration-[220ms]"
                style={{ boxShadow: '0 8px 24px rgba(255,189,118,0.35)' }}
              >
                {t('hero.cta')}
                <ArrowRight size={18} />
              </a>
              <a
                href="#platform"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-[999px] border border-[#99B0B5]/50 text-[#FFFBF7] font-semibold text-base hover:bg-[#FFFBF7]/10 transition-all duration-[220ms]"
              >
                <Play size={15} className="fill-[#FFFBF7]" />
                {t('hero.demoLink')}
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-5 pt-2">
              {[
                { icon: <ShieldCheck size={14} />, key: 'hero.trust1' },
                { icon: <Star size={14} className="fill-[#FFBD76] text-[#FFBD76]" />, key: 'hero.trust2' },
                { icon: <Users size={14} />, key: 'hero.trust3' },
              ].map((item) => (
                <span
                  key={item.key}
                  className="inline-flex items-center gap-1.5 text-[#FFF6E9]/60 text-xs font-medium"
                >
                  <span className="text-[#FFBD76]">{item.icon}</span>
                  {t(item.key as any)}
                </span>
              ))}
            </div>

            {/* Stats strip */}
            <div className="flex flex-wrap gap-8 border-t border-[#99B0B5]/20 pt-7 mt-1">
              {[
                { key: 'hero.stat1', label: 'hero.stat1Label' },
                { key: 'hero.stat2', label: 'hero.stat2Label' },
                { key: 'hero.stat3', label: 'hero.stat3Label' },
                { key: 'hero.stat4', label: 'hero.stat4Label' },
              ].map((stat) => (
                <div key={stat.key} className="flex flex-col gap-1">
                  <span className="text-2xl font-bold text-[#FFBD76] tracking-[-0.02em]">
                    {t(stat.key as any)}
                  </span>
                  <span className="text-xs text-[#FFF6E9]/55 font-medium">{t(stat.label as any)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right column — Dashboard ─────────────────────────── */}
          <div
            className="relative hidden lg:block"
            style={{
              animation: 'heroFloat 6s ease-in-out infinite',
            }}
          >
            {/* Glow backdrop */}
            <div
              className="absolute -inset-6 rounded-[32px] blur-2xl opacity-25"
              style={{ background: 'radial-gradient(ellipse at center, #FFBD76 0%, #27AE9A 100%)' }}
              aria-hidden="true"
            />
            <DashboardPreview />
          </div>
        </div>
      </div>

      {/* Floating animation keyframes */}
      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </section>
  )
}
